import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    OnInit,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import {
    DragAndDropComponent,
    DragAndDropTypeEnum,
} from '@connect-services/client/components/drag-and-drop/drag-and-drop.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { SubscriptionService } from '@connect-services/client/services/subscription.service';
import { ValidatorsService } from '@connect-services/client/services/validators.service';
import {
    ButtonComponent,
    ButtonType,
    IconPosition,
} from '@connect-services/src/lib/components/button/button.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { DepositFolder, Standard, Subscription } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { FormErrorEnum } from '@shared/client/models/form.model';
import { FormService } from '@shared/client/services/form.service';

export class AddDepositModalForm extends FormGroup<{
    depositFolder: FormControl<string | null>;
    file: FormControl<File | null>;
    name: FormControl<string>;
    standard: FormControl<string | null>;
}> {}

export type AddDepositModalData = {
    depositCount: number;
    depositFolder: DepositFolder;
    name: string;
    standard?: Standard | null;
    title: string;
};

enum AddDepositStepEnum {
    EMPTY_CREATION = 2,
    FILE_IMPORT = 1,
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, ComplexModalTemplate, DragAndDropComponent, InputTextComponent, RessourcesModule],
    selector: 'app-add-deposit-modal',
    templateUrl: './add-deposit-modal.component.html',
})
export class AddDepositModalComponent implements OnInit {
    public readonly currentStep: WritableSignal<AddDepositStepEnum> = signal<AddDepositStepEnum>(
        AddDepositStepEnum.FILE_IMPORT,
    );
    public readonly maxImportSizeOctet: Signal<number> = computed(() => {
        if (!this.subscription()?.maxImportSizeInKo) {
            return Number.POSITIVE_INFINITY;
        }

        const kibToMibMultiplier = 1024;

        return this.subscription()!.maxImportSizeInKo! * kibToMibMultiplier;
    });
    public readonly subscription: WritableSignal<Subscription | undefined> = signal<Subscription | undefined>(
        undefined,
    );

    protected readonly acceptedDepositFileExtensions = ['.csv', '.gpkg', '.kmz', '.xls', '.xlsx', '.zip'];
    protected readonly acceptedDepositFileFormatTranslationKeys = [
        'acceptedFormats.shapefile',
        'acceptedFormats.kml',
        'acceptedFormats.kmz',
        'acceptedFormats.geopackage',
        'acceptedFormats.geojson',
        'acceptedFormats.gpx',
        'acceptedFormats.gml',
        'acceptedFormats.excel',
        'acceptedFormats.csv',
        'acceptedFormats.geocsv',
        'acceptedFormats.dwg',
        'acceptedFormats.dxf',
    ] as const;
    protected addDepositModalDataForm!: AddDepositModalForm;
    protected readonly AddDepositStepEnum = AddDepositStepEnum;
    protected readonly ButtonType = ButtonType;
    protected data: AddDepositModalData = inject(MAT_DIALOG_DATA);
    protected dialogRef = inject(MatDialogRef<AddDepositModalComponent>);
    protected readonly DragAndDropTypeEnum = DragAndDropTypeEnum;
    protected readonly IconPosition = IconPosition;
    protected readonly subscriptionService = inject(SubscriptionService);

    private readonly formService = inject(FormService);

    public constructor() {
        effect(() => {
            this.currentStep();
            this.addDepositModalDataForm.controls.name.reset();
        });
    }

    public getNameErrors(): string[] {
        const errors: ValidationErrors | null = this.addDepositModalDataForm.controls.name.errors;

        if (!errors) {
            return [];
        }

        return Object.entries(errors)
            .filter(([key]) => key !== FormErrorEnum.REQUIRED)
            .map(([key, value]) =>
                this.formService.getErrorMessage({
                    key,
                    value,
                }),
            );
    }

    public async ngOnInit(): Promise<void> {
        this.initForm();

        this.subscription.set(await this.subscriptionService.getSubscription());
    }

    protected getFile(files: File[]): void {
        if (!files.length) {
            this.addDepositModalDataForm.controls.file.setValue(null);
            this.addDepositModalDataForm.controls.name.setValue('');

            return;
        }

        this.addDepositModalDataForm.controls.file.setValue(files[0]);

        const fileNameWithoutExtension: string = files[0].name.split('.').slice(0, -1).join('.');
        this.addDepositModalDataForm.controls.name.setValue(fileNameWithoutExtension);
    }

    protected onClickEvent(modalResult: ModalResultEnum): void {
        if (modalResult === ModalResultEnum.CANCEL) {
            this.dialogRef.close(null);

            return;
        }
        this.dialogRef.close(this.addDepositModalDataForm);
    }

    private initForm(): void {
        this.addDepositModalDataForm = new AddDepositModalForm({
            depositFolder: new FormControl<string | null>(this.data.depositFolder.id ?? null, {
                nonNullable: true,
            }),
            file: new FormControl<File | null>(null, {
                nonNullable: false,
            }),
            name: new FormControl<string>(this.data.name, {
                nonNullable: true,
                validators: [
                    Validators.required,
                    ValidatorsService.forbiddenCharsValidator(FormService.FORBIDDEN_CHARS),
                ],
            }),
            standard: new FormControl<string | null>(this.data.standard?.id ?? null, {
                nonNullable: false,
            }),
        });
    }
}
